import { type QueryParams } from "@/api";
import satisfy from "@/utils/tsUtils/satisfy";
import { endpointMock, queryMocks } from "./mockData";

export const metadataCatalog = {
  authority: {
    key: "authority",
    endpoint: "/api/v3/content/delivery/metadata/authority/",
  },
  concept: {
    key: "concept",
    endpoint: "/api/v3/content/delivery/metadata/concept/[id]/",
  },
  document: {
    key: "document",
    endpoint: "/api/v3/content/delivery/metadata/document/",
  },
  documentViewmodel: {
    key: "documentViewmodel",
    endpoint: "/api/v3/metadata/delivery/document/[id]/",
  },
  path: {
    key: "path",
    endpoint: "/api/v3/content/delivery/metadata/path/",
  },
  publication: {
    key: "publication",
    endpoint: "/api/v3/content/delivery/metadata/publication/",
  },
  readingSkill: {
    key: "readingSkill",
    endpoint: "/api/v3/content/delivery/metadata/readingskill/",
  },
  readingSkills: {
    key: "readingSkills",
    endpoint: "/api/v3/metadata/reading-skills/",
  },
  readingSkillCrosswalks: {
    key: "readingSkillCrosswalks",
    endpoint: "/api/v3/content/delivery/metadata/readingskill/[id]/crosswalk",
  },
  section: {
    key: "section",
    endpoint: "/api/v3/content/delivery/metadata/section/[id]/",
  },
  standard: {
    key: "standard",
    endpoint: "/api/v3/content/delivery/metadata/standard/[id]/",
  },
  standardCrosswalks: {
    key: "standardCrosswalks",
    endpoint: "/api/v3/content/delivery/metadata/standard/[id]/crosswalk",
  },
  topic: {
    key: "topic",
    endpoint: "/api/v3/content/delivery/metadata/topic/[id]/",
  },
} as const;

export const celeryCatalog = {
  celeryRunTask: {
    key: "celeryRunTask",
    endpoint: "/celery/task/run/",
  },
  celeryRevokeTask: {
    key: "celeryRevokeTask",
    endpoint: "/celery/task/[taskId]/revoke/",
  },
  celeryTaskMetadata: {
    key: "celeryTaskMetadata",
    endpoint: "/celery/task/[taskId]/",
  },
  celeryTaskLogs: {
    key: "celeryTaskLogs",
    endpoint: "/celery/task/[taskId]/logs/",
  },
  celeryTaskRequestLogsDownload: {
    key: "celeryTaskRequestLogsDownload",
    endpoint: "/celery/logs/download/",
  },
  celeryTaskRequestLogsDownloadStatus: {
    key: "celeryTaskRequestLogsDownloadStatus",
    endpoint: "/celery/logs/[downloadTaskId]/status/",
  },
  celeryTaskDownloadLogs: {
    key: "celeryTaskDownloadLogs",
    endpoint: "/celery/logs/[downloadTaskId]/download/",
  },
} as const;

// These endpoints are supposed to be used
// with Parnassus API's base URL
export const challengesCatalog = {
  challengeArticle: {
    key: "challengeArticle",
    endpoint: "/articles/content/[contentId]/",
  },
} as const;

export const contentDeliveryCatalog = {
  singleContent: {
    key: "singleContent",
    endpoint: "/api/v3/content/delivery/single-content/[id]/",
    mockData: endpointMock.singleContent,
  },
  contentDelivery: {
    key: "contentDelivery",
    endpoint: "/api/v3/content/delivery/",
    mockData: endpointMock.contentDelivery,
  },
  standardAggregations: {
    key: "standardAggregations",
    endpoint: "/api/v3/content/delivery/standards-aggregations/",
  },
} as const;

export const nightingaleCatalog = {
  assignmentsAndPrintsData: {
    key: "assignmentsAndPrintsData",
    endpoint: "/apps/nightingale/usage-report/v1/assignments-with-prints",
  },
  classroomPowerWordsEngagement: {
    key: "classroomPowerWordsEngagement",
    endpoint: "/apps/nightingale/usage/v1/classroom-power-words-engagement",
  },
  classroomReadingLevel: {
    key: "classroomReadingLevel",
    endpoint: "/apps/nightingale/usage/v1/usage/classroom-reading-level/",
  },
  districtEngagementHighlights: {
    key: "districtEngagementHighlights",
    endpoint: "/apps/nightingale/usage-report/v1/district-highlights",
  },
  readingSkillsPerformance: {
    key: "readingSkillsPerformance",
    endpoint: "/apps/nightingale/reading-skills/v1/skills-performance/",
  },
  schoolEngagementHighlights: {
    key: "schoolEngagementHighlights",
    endpoint: "/apps/nightingale/usage-report/v1/school-highlights",
  },
  schoolUsageData: {
    key: "schoolUsageData",
    endpoint: "/apps/nightingale/usage-report/v1/school-data",
  },
  studentActivity: {
    key: "studentActivity",
    endpoint: "/apps/nightingale/usage/v1/student-activity",
  },
  studentPowerWordsEngagement: {
    key: "studentPowerWordsEngagement",
    endpoint: "/apps/nightingale/usage/v1/student-power-words-engagement",
  },
  studentReadingLevel: {
    key: "studentReadingLevel",
    endpoint: "/apps/nightingale/usage/v1/usage/student-reading-level/",
  },
  studentsActivityMetrics: {
    key: "studentsActivityMetrics",
    endpoint: "/apps/nightingale/usage/v1/students-activity-metrics",
  },
  teacherUsageData: {
    key: "teacherUsageData",
    endpoint: "/apps/nightingale/usage-report/v1/teacher-data",
  },
  weeklyHeadersViewed: {
    key: "weeklyHeadersViewed",
    endpoint: "/apps/nightingale/usage/v1/weekly-headers-viewed",
  },
  weeklyQuizzesSubmitted: {
    key: "weeklyQuizzesSubmitted",
    endpoint: "/apps/nightingale/usage/v1/weekly-quizzes-submitted",
  },
} as const;

const catalogConst = {
  ...metadataCatalog,
  ...celeryCatalog,
  ...contentDeliveryCatalog,
  ...nightingaleCatalog,
  ...challengesCatalog,
  adminConsoleDirectoryCsvGenerationStatus: {
    key: "adminConsoleDirectoryCsvGenerationStatus",
    endpoint:
      "/api/v3/user/[userUniqueId]/directory/[directoryType]/csv-generation-status/",
  },
  adminConsoleDirectoryCsvGeneration: {
    key: "adminConsoleDirectoryCsvGeneration",
    endpoint:
      "/api/v3/user/[userUniqueId]/directory/[directoryType]/csv-generation/",
  },
  articles: {
    key: "articles",
  },
  adminConsoleDirectory: {
    key: "adminConsoleDirectory",
    endpoint: "/api/v3/user/[userUniqueId]/directory/[directoryType]/",
  },
  attributes: {
    key: "attributes",
    endpoint: "/api/v3/user/attributes/",
  },
  classroomsThroughUser: {
    key: "classroomsThroughUser",
    endpoint: "/api/v3/user/[id]/classroom/",
  },
  classroom: {
    key: "classroom",
    endpoint: "/api/v3/classroom/[id]/",
  },
  classroomCodeLookup: {
    key: "classroomCodeLookup",
    endpoint: "/api/v3/classroom/code/[classCode]/",
  },
  contentPreference: {
    key: "contentPreference",
    endpoint: "/api/v3/user/[id]/content-preference/",
  },
  contentRecommendation: {
    key: "contentRecommendation",
    endpoint: "/seti/tag2content_recommender/",
  },
  contentLeveler: {
    key: "contentLeveler",
    endpoint: "/seti/leveler/level-article/",
  },
  contentTextAnalysisReadingLevel: {
    key: "contentTextAnalysisReadingLevel",
    endpoint: "/seti/text-analysis/reading-level/",
  },
  district: {
    key: "district",
    endpoint: "/api/v2/district/[id]/",
  },
  districtAdmin: {
    key: "districtAdmin",
    endpoint: "/api/v2/user/[id]/district-admin/",
  },
  districtBulkProcessingConfiguration: {
    key: "districtBulkProcessingConfiguration",
    endpoint: "/api/v2/district-bulk-processing-configuration/",
  },
  draftContent: {
    key: "draftContent",
    endpoint: "/api/v3/management/content/",
  },
  eventRecord: {
    key: "eventRecord",
    endpoint: "/api/v2/event-record/",
    mockData: endpointMock.eventRecord,
  },
  feature: {
    key: "feature",
    endpoint: "/api/v2/user/[id]/feature/",
    mockData: endpointMock.feature,
  },
  graphicOrganizer: {
    key: "graphicOrganizer",
    endpoint: "/api/v3/graphic-organizers/",
  },
  graphicOrganizerResponses: {
    key: "graphicOrganizerResponses",
    endpoint: "/api/v3/graphic-organizers/responses/",
  },
  license: {
    key: "license",
    endpoint: "/api/v2/user/[id]/license/",
  },
  metadataSearch: {
    key: "metadataSearch",
    endpoint: "/api/v3/content/delivery/metadata/search/",
  },
  parent: {
    key: "parent",
    endpoint: "/api/v2/user/[id]/parent/",
  },
  personalPage: {
    key: "personalPage",
    endpoint: "/agatha/api/v2/personal-page/home/",
    mockData: endpointMock.personalPage,
    mockQueryParams: queryMocks.personalPage,
  },
  promotedContent: {
    key: "promotedContent",
    endpoint: "/api/v3/content/delivery/promoted-content/",
    mockData: endpointMock.promotedContent,
    mockQueryParams: queryMocks.promotedContent,
  },

  principal: {
    key: "principal",
    endpoint: "/api/v2/principal/[id]/",
  },
  principalThroughUser: {
    key: "principalThroughUser",
    endpoint: "/api/v2/user/[id]/principal/",
  },
  recommendations: {
    key: "recommendations",
    endpoint: "/api/v3/content/delivery/recommendations/",
    mockData: endpointMock.recommendations,
    mockQueryParams: queryMocks.recommendations,
  },
  rosterClassroom: {
    key: "rosterClassroom",
    endpoint: "/api/v2/roster-classroom/",
    mockData: endpointMock.rosterClassroom,
    mockQueryParams: queryMocks.rosterClassroom,
  },
  schoolThroughUser: {
    key: "schoolThroughUser",
    endpoint: "/api/v2/user/[id]/school/",
    mockData: endpointMock.schoolThroughUser,
  },
  schoolThroughTeacher: {
    key: "schoolThroughTeacher",
    endpoint: "/api/v3/user/[id]/teacher/school/",
  },
  school: {
    key: "school",
    endpoint: "/api/v3/user/[id]/school/",
  },
  schoolAdminSchoolAffiliation: {
    key: "schoolAdminSchoolAffiliation",
    endpoint: "/api/v3/user/[unique_id]/school-admin/school-affiliations/",
  },
  schoolRecommendation: {
    key: "schoolRecommendation",
    endpoint: "/api/v3/school-recommendation/",
    mockData: endpointMock.schoolRecommendation,
  },
  schoolLicense: {
    key: "schoolLicense",
    endpoint: "/api/v3/school/[id]/license/",
  },
  schoolThroughId: {
    key: "schoolThroughId",
    endpoint: "/api/v2/school/[id]/",
  },
  search: {
    key: "search",
    endpoint: "/api/v3/content/delivery/search/",
    mockData: endpointMock.search,
    mockQueryParams: queryMocks.search,
  },
  searchInterpret: {
    key: "searchInterpret",
    endpoint: "/api/v3/content/delivery/search/interpret/",
    mockData: endpointMock.searchIntepret,
  },
  student: {
    key: "student",
    endpoint: "/api/v2/user/[id]/student/",
  },
  session: {
    key: "session",
    endpoint: "/api/v2/session/",
  },
  taxonomy: {
    key: "taxonomy",
    endpoint: "/api/v3/metadata/delivery/document/[document_id]/",
  },
  taxonomyRecommendation: {
    key: "taxonomyRecommendation",
    endpoint: "/seti/tag2tag_recommender/",
  },
  teacher: {
    key: "teacher",
    endpoint: "/api/v2/teacher/[id]/",
  },
  teachers: {
    key: "teachers",
    endpoint: "/api/v2/teacher/",
  },
  teacherSchoolAffiliation: {
    key: "teacherSchoolAffiliation",
    endpoint: "/api/v3/user/[unique_id]/teacher/school-affiliations/",
  },
  teacherThroughUser: {
    key: "teacherThroughUser",
    endpoint: "/api/v2/user/[id]/teacher/",
    mockData: endpointMock.teacherThroughUser,
  },
  teacherThroughSchool: {
    key: "teacherThroughSchool",
    endpoint: "/api/v3/school/[school_id]/teacher/[teacher_id]",
    mockQueryParams: queryMocks.teacherThroughSchool,
    mockData: endpointMock.teacherThroughSchool,
  },
  trafficCop: {
    key: "trafficCop",
    endpoint: "/api/v3/content/delivery/traffic-cop/",
    mockData: endpointMock.trafficCop,
  },
  schoolAdmin: {
    key: "schoolAdmin",
    endpoint: "/api/v2/school-admin/",
  },
  user: {
    key: "user",
    endpoint: "/api/v3/user/[id]/",
  },
  userExperiment: {
    key: "userExperiment",
    endpoint: "/api/v3/user/[id]/experiment/",
    mockData: endpointMock.userExperiment,
  },
  userSchool: {
    key: "userSchool",
    endpoint: "/api/v3/user/[id]/school/",
    mockData: endpointMock.userSchool,
    mockQueryParams: queryMocks.userSchool,
  },
  content: {
    key: "content",
    endpoint: "/api/v3/content/",
  },
} as const;

export type ApiCatalogKey = keyof typeof catalogConst;

/**
 * ensure that the key value in each catalog matches their key
 * and is therefore unique
 */
type UniqueKeyCatalog = {
  [Key in keyof typeof catalogConst]: {
    key: Key;
    endpoint?: `/${string}`;
    mockData?: unknown;
    mockQueryParams?: QueryParams;
  };
};

export const ApiCatalog = satisfy<UniqueKeyCatalog>()(catalogConst);

interface MockData {
  mockData: unknown;
}

type ApiCatalogType = typeof ApiCatalog;

/**
 * if we have mockdata for this api this will be able to type your return type
 **/
export type HasMockData<ApiKey extends ApiCatalogKey> =
  ApiCatalogType[ApiKey] extends MockData
    ? ApiCatalogType[ApiKey]["mockData"]
    : unknown;

interface MockQueryParams {
  mockQueryParams: QueryParams;
}

/**
 * if we have mockdata for this api this will be able to type
 * your approved query params
 **/
export type HasQueryParams<ApiKey extends ApiCatalogKey> =
  ApiCatalogType[ApiKey] extends MockQueryParams
    ? ApiCatalogType[ApiKey]["mockQueryParams"]
    : QueryParams;
